import React, { useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import { FaSave, FaEnvelope, FaSpinner } from 'react-icons/fa';
import ClientLayout from '../layouts/ClientLayout';
import { API_URL } from '../config';

const authHeaders = () => ({ Authorization: `Bearer ${localStorage.getItem('token') || ''}` });

function ClientSettings({ onLogout }) {
  const [user, setUser] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [prefs, setPrefs] = useState({ email_quotes: true, email_jobs: true, email_invoices: true });

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        const r = await fetch(`${API_URL}/users/me`, { headers: authHeaders() });
        const data = await r.json();
        if (!cancelled && data.success && data.user) {
          setUser(data.user);
          const p = data.user.notification_prefs || {};
          setPrefs({
            email_quotes: p.email_quotes !== false,
            email_jobs: p.email_jobs !== false,
            email_invoices: p.email_invoices !== false
          });
        }
      } catch (err) {
        console.error('Could not load your settings:', err);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => { cancelled = true; };
  }, []);

  const saveSettings = async () => {
    setSaving(true);
    try {
      const r = await fetch(`${API_URL}/users/me`, {
        method: 'PUT',
        headers: { ...authHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify({ notification_prefs: prefs })
      });
      const data = await r.json();
      if (data.success) {
        toast.success('✅ Notification settings saved');
        // Keep the cached login in step so the bell/email behaviour matches.
        const stored = JSON.parse(localStorage.getItem('user') || '{}');
        localStorage.setItem('user', JSON.stringify({ ...stored, notification_prefs: prefs }));
      } else {
        toast.error(data.error || 'Could not save your settings');
      }
    } catch (err) {
      console.error('Could not save your settings:', err);
      toast.error('Could not save your settings');
    } finally {
      setSaving(false);
    }
  };

  return (
    <ClientLayout activeTab="settings" onLogout={onLogout} user={user}>
      <div className="flex items-center gap-4 mb-6">
        <div>
          <h1 className="text-2xl font-bold" style={{ color: '#0A1929' }}>⚙️ Settings</h1>
          <p className="text-sm" style={{ color: '#4A5568' }}>Manage your preferences</p>
        </div>
      </div>

      <div className="bg-white rounded-xl border p-6" style={{ borderColor: '#E8ECEF' }}>
        <h3 className="font-bold mb-4" style={{ color: '#0A1929' }}>Notifications</h3>
        
        {loading ? (
          <p className="text-sm text-gray-500 flex items-center gap-2">
            <FaSpinner className="animate-spin" /> Loading your settings...
          </p>
        ) : (
          <div className="space-y-4">
            {[
              { key: 'email_quotes', label: 'New quotes', desc: 'Email me when a quote is sent for me to review' },
              { key: 'email_jobs', label: 'Job updates', desc: 'Email me when a job is allocated, collected or delivered' },
              { key: 'email_invoices', label: 'Invoices and proformas', desc: 'Email me when a document is issued to my company' }
            ].map((row) => (
              <div key={row.key} className="flex items-center justify-between p-4 border rounded-lg" style={{ borderColor: '#E8ECEF' }}>
                <div className="flex items-center gap-3">
                  <FaEnvelope style={{ color: '#94A3B8' }} />
                  <div>
                    <p className="font-medium" style={{ color: '#0A1929' }}>{row.label}</p>
                    <p className="text-xs" style={{ color: '#4A5568' }}>{row.desc}</p>
                  </div>
                </div>
                <button
                  type="button"
                  aria-label={row.label}
                  onClick={() => setPrefs((prev) => ({ ...prev, [row.key]: !prev[row.key] }))}
                  className={`w-12 h-6 rounded-full transition-colors ${prefs[row.key] ? 'bg-green-500' : 'bg-gray-300'}`}
                >
                  <div className={`w-5 h-5 bg-white rounded-full transition-transform ${prefs[row.key] ? 'translate-x-6' : 'translate-x-1'}`}></div>
                </button>
              </div>
            ))}

            <div className="rounded-lg p-4 text-xs" style={{ backgroundColor: '#f8fafc', color: '#4A5568' }}>
              Notifications in the bell (top right) are always on — these switches only control the emails.
              SMS updates are not available yet.
            </div>
          </div>
        )}

        <div className="mt-6 pt-6 border-t" style={{ borderColor: '#E8ECEF' }}>
          <button
            type="button"
            onClick={saveSettings}
            disabled={saving || loading}
            className="px-6 py-2 rounded-lg text-sm font-medium text-white transition-colors disabled:opacity-50 inline-flex items-center gap-2"
            style={{ backgroundColor: '#00A86B' }}
          >
            {saving ? <FaSpinner className="animate-spin" size={14} /> : <FaSave size={14} />}
            {saving ? 'Saving...' : 'Save Settings'}
          </button>
        </div>
      </div>
    </ClientLayout>
  );
}

export default ClientSettings;
